import { PageHeader } from "@/components/layout/page-header";
import { PageTitle } from "@/components/layout/page-title";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@radix-ui/react-separator";
import type { ReactNode } from "react";
import * as React from "react";
import { Link } from "react-router-dom";

export interface BreadcrumbItemProps {
  /**
   * The label to display for the breadcrumb item
   */
  label: string;
  /**
   * Optional href for the breadcrumb item
   * If not provided, the item will be rendered as text
   */
  href?: string;
}

export interface PageLayoutProps {
  /**
   * The title of the page
   */
  title: string;
  /**
   * Optional description to display below the title
   */
  description?: string;
  /**
   * Optional action buttons to display in the header
   */
  actions?: ReactNode;
  /**
   * The main content of the page
   */
  children: ReactNode;
  /**
   * Optional breadcrumb items
   */
  breadcrumbs?: BreadcrumbItemProps[];
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Optional maximum width for the container
   * @default "max-w-7xl"
   */
  maxWidth?: string;
}

export function PageLayout({
  title,
  description,
  actions,
  children,
  breadcrumbs,
  className = "",
  maxWidth = "max-w-7xl",
}: Readonly<PageLayoutProps>) {
  return (
    <div className={`container ${maxWidth} mx-auto py-8 ${className}`}>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((item, index) => (
                  <React.Fragment key={index}>
                    <BreadcrumbItem>
                      {item.href ? (
                        <BreadcrumbLink asChild>
                          <Link to={item.href}>{item.label}</Link>
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>
      </header>

      <PageHeader>
        <PageTitle
          title={title}
          description={description}
          className="text-left"
        />
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </PageHeader>

      <div className="mt-6">{children}</div>
    </div>
  );
}
